import PageService from '@desygner/ui-editor-data-access-pages';

import { convertUnitsToPixels } from 'assets/js/helpers/convert-units-to-pixels';

export class PageFactory extends PageService {
  constructor () {
    super({
      baseUrl: INKIVE.C.API_URL,
      bookId: INKIVE.C.BOOK.id,
      credentials: getCredentials(),
      storageName: 'db-editor-pages'
    });
  }

  updateCredentials (credentials) {
    this.updateCredentials(credentials);
  }

  async getProjectData () {
    const projectIsTemplate = INKIVE.C.BOOK.book_type === INKIVE.C.BOOK_TYPE.TEMPLATE;
    const projectDataPromise = projectIsTemplate ? super.getTemplateData() : super.getDesignData();
    const projectData = await projectDataPromise;

    this.setActivePage(projectData);

    return projectData;
  }

  setActivePage (projectData) {
    const pages = projectData?.pages;

    if (!pages?.length) return;

    const activePageId = INKIVE.Tool._current_design_id;
    const activePageNumber = INKIVE.page || INKIVE.Tool._current_page_number;

    if (activePageId && !activePageNumber) {
      const index = pages.findIndex(page => page.id === activePageId);
      INKIVE.Tool._current_page_number = index !== -1 ? index + 1 : 1;
    } else if (!activePageId && activePageNumber) {
      const index = activePageNumber - 1;
      INKIVE.Tool._current_design_id = pages[index].id;
    } else if (!activePageId && !activePageNumber) {
      const index = 0;
      INKIVE.Tool._current_design_id = pages[index].id;
      INKIVE.Tool._current_page_number = 1;
    }
  }

  isCurrentPage (id) {
    return Number(INKIVE.PageFactory.getCurrentPageId()) === Number(id);
  }

  getCurrentPage () {
    return INKIVE.ProjectFacade.get('design', {
      design_id: INKIVE.Tool._current_design_id
    }) || INKIVE.ProjectFacade.get('design', {
      page_number: INKIVE.Tool._current_page_number
    });
  }

  getCurrentPageId () {
    return INKIVE.Tool._current_design_id;
  }

  getActivePageId () {
    return String(INKIVE.Tool._current_design_id);
  }

  getActivePageIndex () {
    return this.getActivePageNumber() - 1;
  }

  getActivePageNumber () {
    return parseInt(INKIVE.Tool._current_page_number);
  }

  getActivePage () {
    return {
      id: this.getActivePageId(),
      index: this.getActivePageIndex(),
      number: this.getActivePageNumber()
    };
  }

  getCurrentPageNumber () {
    return this.getCurrentPage()?.page_number;
  }

  getCurrentPageData () {
    const { id, page_number: index } = INKIVE.PageFactory.getCurrentPage();
    const svg = document.getElementById('svgcontent');
    const content = INKIVE.Helpers.serializeToString(svg);

    return { id, index, content };
  }

  async getPage (id) {
    const response = await super.getPage(id);
    const pageData = this.getPageData(id);

    return {
      ...response,
      index: pageData.page_number ?? response.index,
      id: response.id || id
    };
  }

  getPageIdByPageNumber (pageNumber) {
    if (pageNumber < 1) throw new Error('Page number must be greater than 0');

    const page = INKIVE.ProjectFacade.get('design', {
      page_number: pageNumber
    });

    if (!page) throw new Error(`Page number ${pageNumber} not found`);

    return page.id;
  }

  async getPageXml (id) {
    const page = await this.getPage(id);

    return svgedit.utilities.text2xml(page.content);
  }

  getPageData (id) {
    return INKIVE.ProjectFacade.get('design', {
      design_id: id
    });
  }

  async getOptimalPageDimensions (pageIds = []) {
    // Uses a decreasing scale factor to find the optimal page dimensions

    const projectData = await this.getProjectData();

    const pages = (projectData?.pages || []).filter(page => (
      !pageIds.length || pageIds.includes(page.id)
    ));

    const dimensions = pages.reduce((acc, page) => {
      const width = convertUnitsToPixels(page.width, page.unit);
      const height = convertUnitsToPixels(page.height, page.unit);

      return {
        width: Math.min(acc.width, width),
        height: Math.min(acc.height, height)
      };
    }, { width: Infinity, height: Infinity });

    return dimensions;
  }
}

function getCredentials () {
  if (!CONFIG.credentials) return null;

  const { token, hash } = CONFIG.credentials;
  return Base64.encode(`${token}:${hash}`);
}
